import { type MultiValue } from "react-select";
import CreatableSelect from "react-select/creatable";

type PennMultiSelectOption = { value: string; label: string };

type PennMultiSelectProps = {
  options: PennMultiSelectOption[];
  /** Selected values. */
  value?: string[];
  onChange?: (value: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** Fired when the menu is scrolled to the bottom — wire to fetch the next page. */
  onMenuScrollToBottom?: () => void;
  /**
   * Fired as the user types (server-side search). When provided, client-side
   * option filtering is disabled — the parent is expected to refetch options.
   */
  onInputChange?: (value: string) => void;
  /** Allow a value that is not present in `options` to be created. */
  allowCreate?: boolean;
  /** Validate a proposed custom value before the create option is shown. */
  isValidNewOption?: (value: string) => boolean;
  formatCreateLabel?: (value: string) => string;
  /** Explain why a typed custom value cannot be created. */
  invalidCreateMessage?: (value: string) => string | null;
  name?: string;
  inputId?: string;
  className?: string;
};

/**
 * Multi-select dropdown (react-select, `isMulti`). Clicking an option selects
 * it and keeps the menu open (`closeMenuOnSelect={false}`); selected options
 * render as removable chips. Styling mirrors PennDropdown. Value is the array
 * of selected option values.
 */
export const PennMultiSelect = ({
  options,
  value = [],
  onChange,
  onBlur,
  placeholder = "Select…",
  error = false,
  isDisabled = false,
  isLoading = false,
  onMenuScrollToBottom,
  onInputChange,
  allowCreate = false,
  isValidNewOption,
  formatCreateLabel,
  invalidCreateMessage,
  name,
  inputId,
  className = "",
}: PennMultiSelectProps) => {
  const optionsByValue = new Map(options.map((option) => [option.value, option]));
  // Preserve saved/custom values even when they are not in the current
  // registered-option result. A mapping edit must never silently drop MD-06
  // recipients merely because they are not registered.
  const selected = value.map(
    (selectedValue) =>
      optionsByValue.get(selectedValue) ?? {
        value: selectedValue,
        label: selectedValue,
      },
  );

  return (
    <CreatableSelect<PennMultiSelectOption, true>
      unstyled
      isMulti
      name={name}
      inputId={inputId}
      options={options}
      value={selected}
      onChange={(opts: MultiValue<PennMultiSelectOption>) =>
        onChange?.(opts.map((o) => o.value))
      }
      onBlur={onBlur}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isLoading={isLoading}
      aria-invalid={error || undefined}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onInputChange={
        onInputChange
          ? (val, meta) => {
              if (meta.action === "input-change") onInputChange(val);
              return val;
            }
          : undefined
      }
      filterOption={onInputChange ? null : undefined}
      isValidNewOption={(inputValue) =>
        allowCreate &&
        (isValidNewOption
          ? isValidNewOption(inputValue)
          : inputValue.trim().length > 0)
      }
      formatCreateLabel={formatCreateLabel}
      noOptionsMessage={({ inputValue }) =>
        invalidCreateMessage?.(inputValue) ?? "No options"
      }
      isSearchable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      className={className}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base) => ({ ...base, fontSize: "0.875rem" }),
        noOptionsMessage: (base) => ({ ...base, fontSize: "0.875rem" }),
        loadingMessage: (base) => ({ ...base, fontSize: "0.875rem" }),
      }}
      classNames={{
        control: (state) =>
          [
            "flex items-center justify-between w-full min-h-[42px] px-3 rounded-xl border text-sm bg-gray-50 transition",
            isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            error
              ? "border-error"
              : state.isFocused
                ? "border-penn-500"
                : "border-gray-200 hover:border-penn-500",
          ].join(" "),
        valueContainer: () => "flex flex-wrap items-center gap-1 py-1",
        placeholder: () => "text-gray-400",
        input: () => "text-penn-text",
        multiValue: () => "flex items-center bg-penn-100 rounded-md overflow-hidden",
        multiValueLabel: () => "px-2 py-0.5 text-xs text-penn-text",
        multiValueRemove: () =>
          "px-1 text-penn-500 hover:bg-penn-500 hover:text-white transition-colors cursor-pointer",
        indicatorsContainer: () => "flex items-center",
        indicatorSeparator: () => "hidden",
        dropdownIndicator: () => "flex items-center px-1 text-gray-400",
        clearIndicator: () => "flex items-center px-1 text-gray-400 hover:text-gray-600 cursor-pointer",
        menu: () => "mt-1 rounded-xl border border-gray-200 bg-white shadow-soft overflow-hidden",
        menuList: () => "py-1 max-h-60 overflow-y-auto",
        option: (state) =>
          [
            "px-3 py-2 text-xs cursor-pointer transition-colors",
            state.isSelected
              ? "bg-penn-500 text-white"
              : state.isFocused
                ? "bg-penn-100 text-penn-text"
                : "text-penn-text",
          ].join(" "),
        noOptionsMessage: () => "px-3 py-2 text-xs text-gray-400",
        loadingMessage: () => "px-3 py-2 text-xs text-gray-400",
      }}
    />
  );
};
